import React, { ReactNode, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useAdmin } from '../../contexts/AdminContext';
import AppLoadingScreen from './AppLoadingScreen';
import ImpersonationBanner from './ImpersonationBanner';

interface TenantOnlyRouteProps {
  children: ReactNode;
}

/**
 * TenantOnlyRoute - Protection component for routes accessible only to tenant users
 *
 * This component provides the same authentication and authorization checks as AuthenticatedLayout
 * but WITHOUT rendering any layout elements (no Sidebar, no Navbar).
 *
 * Use this for pages like onboarding that need tenant-only access but have their own custom layout.
 */
const TenantOnlyRoute: React.FC<TenantOnlyRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: configLoading } = useAppConfig();
  const { isHostAdmin, isLoading: adminLoading } = useAdmin();
  const location = useLocation();

  // Once children have rendered successfully, don't unmount them for subsequent loading state
  // changes (e.g., refreshConfig calls during photo upload). Matches AppInitializer's showContent
  // pattern — a loading flip after initial render must not destroy the onboarding wizard tree.
  const hasRenderedRef = useRef(false);

  const allLoaded = !authLoading && !configLoading && !adminLoading;

  if (allLoaded) {
    hasRenderedRef.current = true;
  }

  // Only show loading screen on initial load, not on subsequent context refreshes
  if (!allLoaded && !hasRenderedRef.current) {
    return <AppLoadingScreen />;
  }

  // Check authentication - redirect to login if not authenticated
  if (!isAuthenticated && !authLoading) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect host admins to admin area - only tenant users can access
  if (isHostAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // User is authenticated and is a tenant user - render children without any layout.
  // ImpersonationBanner renders nothing unless a host admin is impersonating; when it
  // does render, it sets --app-banner-height on <html> so the global CSS offset pushes
  // fixed full-screen overlays (like the onboarding wizard) below it. This keeps the
  // "Stop Impersonation" button reachable even when setup isn't complete and the user
  // is locked into the setup guide.
  return (
    <>
      <ImpersonationBanner />
      {children}
    </>
  );
};

export default TenantOnlyRoute;
